import {debounce, LinearProgress, ThemeProvider} from '@mui/material';
import {BrowserRouter, useLocation} from 'react-router-dom';
import ErrorBoundary from 'shared/ErrorBoundary';
import theme from 'shared/theme';
import {QueryClient, QueryClientProvider} from "react-query";
import {LicenseInfo} from "@mui/x-license-pro";
import React, {useEffect, Suspense, useState, useCallback} from "react";
import {DynamicRouter} from "shared/DynamicRouter";
import Layout from "shared/layout";
import {useRouteStore} from "shared/store/RouteConfigStore";
import {GlobalModals} from "shared/components/GlobalModals";
import {MainRoutes} from "~/Routes/MainRoutes";
import {SignRoutes} from "~/Routes/SignRoutes";
import {useGlobalConfigStore} from "shared/store/GlobalConfigStore";
import {setup} from "shared/libs/axios";

LicenseInfo.setLicenseKey('4710a3119913b38e70c5bdde0aad559eT1JERVI6NDMwNTgsRVhQSVJZPTE2ODMyNTE3MDAwMDAsS0VZVkVSU0lPTj0x');
//LicenseInfo.setLicenseKey(`${process.env.REACT_APP_X_LICENSE_PRO}`);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  }
});

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

const ErrorBoundaryWithRouteReset: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

// 동적으로 <title> 변경 컴포넌트
const TitleManager = () => {
  const location = useLocation();
  const { pageDepths } = useRouteStore();

  useEffect(() => {
    let baseTitle = 'AI 실증지원';
    let navTitle = '';
    // 뎁스 정보가 모두 없으면 메인
    if (!pageDepths?.depth1 && !pageDepths?.depth2 && !pageDepths?.depth3) {
      document.title = `인공지능산업융합사업단 - ${baseTitle}`;
      return;
    }
    if (pageDepths?.depth1 === 'AI 실증지원') {
      navTitle = [pageDepths.depth2, pageDepths.depth3, pageDepths.title]
        .filter(Boolean)
        .join(' > ');
    }else if (pageDepths?.depth1 === '마이페이지' && pageDepths?.depth2 === 'AI 실증지원') {
      navTitle = [pageDepths.depth1, pageDepths.depth2, pageDepths.depth3, pageDepths.title]
        .filter(Boolean)
        .join(' > ');
    } else {
      navTitle = [pageDepths?.depth1, pageDepths?.depth2]
        .filter(Boolean)
        .join(' > ');
    }
    document.title = navTitle ? `${baseTitle} > ${navTitle}` : baseTitle;
  }, [location.pathname, pageDepths]);
  return null;
};

const App = () => {
  const routeStore = useRouteStore()
  const configStore = useGlobalConfigStore()

  const handlerResize = useCallback((e) => {
    configStore.setDevice(isMobileDevice()? 'mobile' : 'pc')
    const isDesktop = isMobileDevice()? window.screen.width> 768 : window.innerWidth > 768
    if (configStore.isDesktop != isDesktop)
      configStore.setScreenMode(isDesktop)
  }, [window.innerWidth])

  useEffect(() => {
    window.addEventListener("resize", handlerResize)
    return () => {
      window.removeEventListener("resize", handlerResize)
    }
  }, [handlerResize])

  useEffect(() => {
    configStore.setDevice(isMobileDevice()? 'mobile' : 'pc')
    routeStore.setMemberServer(`${process.env.REACT_APP_DOMAIN_MEMBER_DEV}`)
    routeStore.setLayout('basic')
    routeStore.setDefaultLayout('basic')
    if (process.env.REACT_APP_MODE == 'prd'){
      console.log = () => {}
      console.warn = () => {}
      console.error = () => {}
    }
  }, [])

  useEffect(() => {
    const domain = process.env.REACT_APP_API
    if (domain) {
      setup({
        baseURL: `${window.location.protocol}//${domain.split('//')[1]}`
      })
    }
  }, [window.location.protocol])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <TitleManager />
          <ErrorBoundaryWithRouteReset>
            <Layout>
              <DynamicRouter
                portalType={"PORTAL_TSP"}
                addRoutes={[
                  ...MainRoutes,
                  ...SignRoutes
                ]}
              />
            </Layout>
            <GlobalModals/>
          </ErrorBoundaryWithRouteReset>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
